import { Suspense, lazy, useEffect } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import theme from "./theme";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import { Loading } from "./components/ui";
import usePageTitle from "./hooks/usePageTitle";
import HomePage from "./pages/HomePage";
import { slugFor } from "./data/projects";

// everything below the home page is split out so the first paint stays light
const WorkPage = lazy(() => import("./pages/WorkPage"));
const ProjectPage = lazy(() => import("./pages/ProjectPage"));
const JournalPage = lazy(() => import("./pages/JournalPage"));
const JournalEntryPage = lazy(() => import("./pages/JournalEntryPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FitnessPage = lazy(() => import("./pages/FitnessPage"));
const EnginePage = lazy(() => import("./pages/EnginePage"));
const PlanPage = lazy(() => import("./pages/PlanPage"));
const ActivityListPage = lazy(() => import("./pages/ActivityListPage"));
const ActivityDetailPage = lazy(() => import("./pages/ActivityDetailPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const HealthPage = lazy(() => import("./pages/HealthPage"));
const TennisPage = lazy(() => import("./pages/TennisPage"));
const ProgressPage = lazy(() => import("./pages/ProgressPage"));
const StudioPage = lazy(() => import("./pages/StudioPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
};

// old /portfolio?project=Dishcare links keep working
const PortfolioRedirect = () => {
  const { search } = useLocation();
  const project = new URLSearchParams(search).get("project");
  const slug = project && slugFor(project);
  return <Navigate to={slug ? `/work/${slug}` : "/work"} replace />;
};

function AppContent() {
  usePageTitle();
  const { pathname } = useLocation();
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <ErrorBoundary key={pathname}>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/work" element={<WorkPage />} />
            <Route path="/work/:slug" element={<ProjectPage />} />
            <Route path="/portfolio" element={<PortfolioRedirect />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/journal/:id" element={<JournalEntryPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/fitness" element={<FitnessPage />} />
            <Route path="/fitness/engine" element={<EnginePage />} />
            <Route path="/fitness/plan" element={<PlanPage />} />
            <Route path="/fitness/activities" element={<ActivityListPage />} />
            <Route path="/fitness/map" element={<MapPage />} />
            <Route path="/fitness/health" element={<HealthPage />} />
            <Route path="/fitness/tennis" element={<TennisPage />} />
            <Route path="/fitness/progress" element={<ProgressPage />} />
            <Route path="/fitness/a/:id" element={<ActivityDetailPage />} />
            <Route path="/studio" element={<StudioPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      {pathname !== "/studio" && <Footer />}
    </>
  );
}

function App() {
  return (
    <ChakraProvider theme={theme}>
      <Router>
        <AppContent />
      </Router>
    </ChakraProvider>
  );
}

export default App;
